import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * AuthFormLayout — centered card used by every dp-auth-ui page.
 * Pencil Fetqo.
 *
 * Layout:
 *   Hero background + centered card ~420w with logo slot, title, form,
 *   secondary footer (links: "Need help?" / "Sign up").
 *
 * Mobile (420+): full-width card with reduced padding.
 */
export interface AuthFormLayoutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  logo?: React.ReactNode
  /**
   * Title rendered ABOVE the card. Optional: omit it when the page renders
   * its own title inside the card (e.g. the unified login surface), so the
   * header block doesn't double up.
   */
  title?: React.ReactNode
  description?: React.ReactNode
  footer?: React.ReactNode
  /** Optional brand panel on the left (for wide screens). */
  sideSlot?: React.ReactNode
  /** Extra classes for the card wrapper (radius / padding overrides). */
  cardClassName?: string
}

export const AuthFormLayout = React.forwardRef<HTMLDivElement, AuthFormLayoutProps>(
  (
    { className, logo, title, description, footer, sideSlot, cardClassName, children, ...props },
    ref,
  ) => (
    <div ref={ref} className={cn('min-h-dvh w-full flex bg-background', className)} {...props}>
      {sideSlot && (
        <aside
          className="hidden lg:flex w-1/2 max-w-[720px] relative overflow-hidden border-r border-ia-border"
          style={{ background: 'linear-gradient(180deg, var(--color-ia-brand) 0%, var(--color-ia-page) 100%)' }}
        >
          {sideSlot}
        </aside>
      )}
      <main className="flex-1 flex items-center justify-center p-4 md:p-6">
        <div className="w-full max-w-[420px] flex flex-col gap-6">
          {logo && <div className="flex justify-center">{logo}</div>}
          {(title || description) && (
            <div className="flex flex-col gap-1 text-center">
              {title && <h1 className="text-display-md text-ia-text">{title}</h1>}
              {description && <p className="text-body-md text-ia-text-secondary">{description}</p>}
            </div>
          )}
          <div
            className={cn(
              'rounded-lg bg-card border border-ia-border shadow-sm p-6 flex flex-col gap-4',
              cardClassName,
            )}
          >
            {children}
          </div>
          {footer && (
            <div className="text-center text-caption text-ia-text-secondary">{footer}</div>
          )}
        </div>
      </main>
    </div>
  ),
)
AuthFormLayout.displayName = 'AuthFormLayout'
